import React from 'react'
import {useState, useContext} from 'react';

import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme, { getTheme } from 'theme';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AccountCircle from '@mui/icons-material/AccountCircle';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import LinearProgress from '@mui/material/LinearProgress';
import Button from '@mui/material/Button';

import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';


import {ReactComponent as WiseviewLogo} from 'assets/wiseview-logo.svg';
import {ReactComponent as WiseviewLogoDark} from 'assets/resilient_logo_dark.svg';
import { logout } from 'utils/api';
import { DataContext } from 'contexts/DataContext';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import 'i18n/config';
import { useTranslation } from 'react-i18next';
import ChangePasswordDialog from 'components/ChangePasswordDialog/ChangePasswordDialog';
import { clearCache } from './api';
import { toast } from 'react-toastify';

const drawerWidth = 240;

interface Props {
    children: React.ReactNode;
    appBar?: boolean
    drawer?: React.ReactNode;
    drawerOpen?: boolean;
    title?: any;
    actions?: any;
}

const AppLayout: React.FC<Props> = (props:Props) => {
        const { t } = useTranslation();

        const [open, setOpen] = useState(props.drawer && props.drawerOpen ? true : false);
        const [isClosing, setIsClosing] = useState(false);
        const showAppBar = props.appBar == null || props.appBar ? true : false;
        const dataContext = useContext(DataContext);
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
        const [showChangePassword, setShowChangePassword] = useState(false);
        const navigate = useNavigate();
        const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
          setAnchorEl(event.currentTarget);
        };
      
        const handleMenuClose = () => {
          setAnchorEl(null);
        };
      
        const handleDrawerClose = () => {
          setIsClosing(true);
          setOpen(false);
        };
      
        const handleDrawerTransitionEnd = () => {
          setIsClosing(false);
        };
      
        const handleDrawerToggle = () => {
          if (!isClosing) {
            setOpen(!open);
          }
        };
            
        const handleLogout = () => {
          logout();
          dataContext.changeToken('');
          dataContext.changeUser({});
          handleMenuClose();
          navigate('/login');
        }

        const handleChangePassword = () => {
         setShowChangePassword(true);
         handleMenuClose();
        }

        const handleLogin = () => {
          handleMenuClose();
          navigate('/login');
        }

        const handleClearCache = () => {
          clearCache().then(response=>{
            toast.success('Cache cleared', {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              });
          });
        }

        return (
          <ThemeProvider theme={getTheme(dataContext.darkMode)}>
            <CssBaseline />

            {dataContext.showLoader && <>
              <LinearProgress color="primary" sx={{position:'absolute',top:64, width:'100%', zIndex: 9999}} />
            </>}

            <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
            <CssBaseline />
            
            {showAppBar &&
            <AppBar
              position="fixed"
              sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '#0C1D27'
                    : theme.palette.background.paper,
                color: (theme) =>
                  theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
              }}
            >
      
              <Toolbar>
              {props.drawer && 
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ mr: 2 }}
                onClick={handleDrawerToggle}
              >
                <MenuIcon />
              </IconButton>}
              <div style={{display:'flex', flexGrow:1, alignItems:'center'}}>
                {dataContext.darkMode ? (
                  <WiseviewLogoDark style={{width:120}} />
                ) : (
                  <WiseviewLogo style={{width:120}} />
                )}

              <FormControl size='small' sx={{minWidth:200, marginLeft:2}}>
                <InputLabel id="organization-select-label">{t("Organization")}</InputLabel>
                <Select
                  labelId="organization-select-label"
                  label={t("Organization")}
                  value={dataContext.currentOrganization ?? ''}
                  onChange={(event) => { dataContext.changeCurrentOrganization(event.target.value) }}
                >
                  {dataContext.organizationList?.map((organization:any, index) => {
                    return <MenuItem key={index} value={organization.id}>{organization.name}</MenuItem>
                  })}
                  
                </Select>
              </FormControl>
            {dataContext.currentOrganization && dataContext.assessmentList &&
              <FormControl size='small' sx={{minWidth:200, marginLeft:2}}>
                <InputLabel id="assessment-select-label">{t("Assessment")}</InputLabel>
                <Select
                  labelId="assessment-select-label"
                  label={t("Assessment")}
                  onChange={(event) => { dataContext.changeCurrentAssessment(event.target.value) }}
                  value={dataContext.currentAssessment ?? ''}
                >
                  {dataContext.assessmentList?.map((assessment:any, index) => {
                    return <MenuItem key={index} value={assessment.id}>{assessment.name}</MenuItem>
                  })}
                </Select>
              </FormControl>}
              </div>

              <IconButton
                size="large"
                aria-label="toggle dark mode"
                onClick={() => dataContext.changeDarkMode(!dataContext.darkMode)}
                color="inherit"
                sx={{ mr: 1 }}
              >
                {dataContext.darkMode ? <LightModeIcon /> : <DarkModeIcon />}
              </IconButton>

              <FormControl size='small' sx={{minWidth:100, marginRight:2}}>
                <InputLabel id="language-select-label">{t("Language")}</InputLabel>
                <Select
                  labelId="language-select-label"
                  label={t("Language")}
                  value={dataContext.language ?? ''}
                  onChange={(event) => dataContext.changeLanguage(event.target.value)}
                >
                  <MenuItem value='en'>English</MenuItem>
                  <MenuItem value='it'>Italiano</MenuItem>
                </Select>
              </FormControl>

              {dataContext.token &&
              <div>
                {dataContext.user?.name ? dataContext.user?.name : dataContext.user?.email}</div>}           
              <div>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
              <AccountCircle />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                 {dataContext.token && <MenuItem onClick={handleChangePassword}>Change password</MenuItem>}
                 {dataContext.token && dataContext.user.id_role===2 && <MenuItem onClick={handleClearCache}>Clear cache</MenuItem>}
                 {dataContext.token && <MenuItem onClick={handleLogout}>Logout</MenuItem>}
                 {!dataContext.token && <MenuItem onClick={handleLogin}>Login</MenuItem>}
              </Menu>
            </div>
                
              </Toolbar>

            </AppBar>
            }
            <Box
              component="nav"
              sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
              aria-label="mailbox folders"
            >
              {props.drawer && 
              <Drawer
                variant="persistent"
                open={open}
                onTransitionEnd={handleDrawerTransitionEnd}
                onClose={handleDrawerClose}
                hideBackdrop={true}
                ModalProps={{
                  keepMounted: true, // Better open performance on mobile.
                }}
                sx={{
                  flexShrink: 0,
                  '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
                }}
              >
                {props.drawer}
              </Drawer>}
       
            </Box>
            
            <Box
              component="main"
              sx={{ 
                flexGrow: 1, 
                overflow: 'auto',
                backgroundColor: (theme) => theme.palette.background.default,
                transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
              marginLeft: `-${drawerWidth}px`,
              ...(open && {
                transition: theme.transitions.create('margin', {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                marginLeft: 0,
              }),
              }}
              >
                {showAppBar && <Toolbar />}
                {props.title && 
                
                <AppBar position="fixed" 
                sx={{
                  marginTop: showAppBar ? '64px' : 0,
                  boxShadow: 'none',
                }}
               >
                <Box 
                sx={{
                  display:'flex', 
                  flexDirection:'row', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding:2,
                    flexGrow: 1, 
                    overflow: 'auto',
                    transition: theme.transitions.create('margin', {
                      easing: theme.transitions.easing.sharp,
                      duration: theme.transitions.duration.leavingScreen,
                    }),
                  marginLeft: open ? `${drawerWidth}px` : 0,
                  borderBottom: (theme) =>
                    `1px solid ${theme.palette.mode === 'dark'
                      ? theme.palette.divider
                      : 'rgba(0, 0, 0, 0.12)'}`,
                  backgroundColor: (theme) => theme.palette.background.default,
                  minHeight: 64,
                  }}>
                    <Box sx={{
                      display:'flex', 
                      flexDirection:'row'}}>
                    {props.title}
                    </Box>
                    <Box>
                    {props.actions && props.actions.map((action:any, index:number) => {
                      const variant = action.variant ? action.variant : 'outlinedPrimary';
                      const isOutlinedPrimary = variant === 'outlinedPrimary';
                      return (
                        <Button
                          key={index}
                          variant="contained"
                          color="primary"
                          size='small'
                          disabled={action.disabled ? action.disabled : false}
                          startIcon={action.icon}
                          onClick={action.action}
                          sx={(theme) => ({
                            marginLeft: 1,
                            borderRadius: '3rem',
                            paddingLeft: 2.5,
                            paddingRight: 2.5,
                            textTransform: 'none',
                            ...(isOutlinedPrimary
                              ? {
                                  border: `1.5px solid ${theme.palette.primary.main}`,
                                  color: theme.palette.primary.main,
                                  backgroundColor: 'transparent',
                                  '&:hover': {
                                    border: `1.5px solid ${theme.palette.primary.main}`,
                                    backgroundColor: 'transparent',
                                  },
                                }
                              : {
                                  border: 'none',
                                  backgroundColor: theme.palette.primary.main,
                                  color: '#000000',
                                  '&:hover': {
                                    backgroundColor: theme.palette.primary.main,
                                  },
                                }),
                          })}
                        >{action.label}</Button>
                      );
                    })}
                    </Box>
                    
                </Box>
                </AppBar>}
                <Box sx={{padding: theme.spacing(3), marginTop: props.title ? 6 : 0}}>
                  <Box sx={{marginTop:'3px', visibility: !dataContext.showLoader ? 'visible': 'visible'}}>
                  {props.children}
                  </Box>
                </Box>

            </Box>
          </Box>
          {showChangePassword && <ChangePasswordDialog showChangePassword={showChangePassword} setShowChangePassword={setShowChangePassword} />}
           <ToastContainer
             toastStyle={dataContext.darkMode ? {
               backgroundColor: '#000000',
               color: '#FFFFFF',
             } : undefined}
           />
          </ThemeProvider>
    )
}

export default AppLayout;